import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { Icon } from 'components/HeroSemantic';

import { getOwnerBankAccounts, saveBankAccount } from 'state/modules/bankAccount';
import EntityDetailsForm from 'components/Form/EntityDetailsForm';
import { BANK_ACCOUNT_FIELDS, BANK_ACCOUNT_TEMPLATES, CURRENCY_OPTIONS } from './constants';

const tabBaseStyle = {
  alignItems: 'center',
  borderRadius: '999px',
  cursor: 'pointer',
  display: 'inline-flex',
  fontSize: '13px',
  fontWeight: 600,
  gap: '8px',
  lineHeight: '18px',
  padding: '8px 16px',
  transition: 'background-color 160ms ease, border-color 160ms ease, color 160ms ease',
  whiteSpace: 'nowrap',
};

const activeTabStyle = {
  ...tabBaseStyle,
  background: 'color-mix(in srgb, var(--tenant-primary) 78%, #000 22%)',
  border: '1px solid color-mix(in srgb, var(--tenant-primary) 78%, #000 22%)',
  color: '#FFFFFF',
};

const inactiveTabStyle = {
  ...tabBaseStyle,
  background: 'transparent',
  border: '1px solid var(--owner-border-strong)',
  color: 'var(--owner-text)',
};

const addTabStyle = active => ({
  ...tabBaseStyle,
  background: active ? 'var(--owner-accent-subtle)' : 'transparent',
  border: `1px dashed ${active ? 'var(--owner-accent-border)' : 'var(--owner-border-strong)'}`,
  color: active ? 'var(--owner-accent-readable)' : 'var(--owner-text-muted)',
});

const statusDotStyle = verified => ({
  background: verified ? 'var(--owner-success)' : 'var(--owner-warning)',
  borderRadius: '999px',
  display: 'inline-block',
  height: '8px',
  width: '8px',
});

const BankAccounts = () => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const [warning, setWarning] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  // Remember the last viewed account tab so "Cancel" on the Add form returns
  // there instead of leaving the user stuck in the add-view.
  const [lastViewedIndex, setLastViewedIndex] = useState(0);
  const bankAccounts = useSelector(state => state.bankAccount.bankAccounts);
  const ownerId = useSelector(state => state.auth.user.id);
  const ownerUsed2FA = useSelector(state => state.auth.used2FA);
  const numOfAccounts = Array.isArray(bankAccounts) ? bankAccounts.length : 0;
  const canAddAnother = numOfAccounts < BANK_ACCOUNT_TEMPLATES.length;
  const addIndex = numOfAccounts; // index used to represent the "add new" pseudo-tab
  // Clamp activeIndex so it never points past the available tabs
  const safeActiveIndex = Math.min(activeIndex, canAddAnother ? addIndex : Math.max(0, numOfAccounts - 1));
  const isAdding = canAddAnother && safeActiveIndex === addIndex;
  const activeTemplate = BANK_ACCOUNT_TEMPLATES[safeActiveIndex] || BANK_ACCOUNT_TEMPLATES[0];
  const activeAccount = bankAccounts && !isAdding ?
    bankAccounts.find(account => account.ownerNumber === activeTemplate.orderNumber)
    : undefined;

  const bankAccountFieldsWithOptions = useMemo(() => BANK_ACCOUNT_FIELDS.reduce((acc, field) => {
    if (field.name === 'currency') {
      acc.push({
        ...field,
        options: CURRENCY_OPTIONS.map(currency => ({
          text: currency.code,
          value: currency.code,
        })),
      });
    }
    else {
      acc.push(field);
    }
    return acc;
  }, []), []);

  useEffect(() => {
    dispatch(getOwnerBankAccounts(ownerId));
  }, [dispatch, ownerId]);

  const handleValuesChanged = updatedValues => {
    if (ownerUsed2FA) {
      // Owners that logged-in with 2FA will get their Bank updates automatically verified
      setWarning(null);
      return;
    }

    const bankAccount = bankAccounts.find(b => b.id === updatedValues.id);

    if (!bankAccount || !bankAccount.verified) {
      // No warning is required for new bank accounts or accounts that are not yet verified
      setWarning(null);
      return;
    }

    // A verified bank account is going to be updated and the Owner didn't use 2FA
    setWarning(formatMessage({ id: 'page.user.bankAccounts.statusChange.warning' }));
  };

  const handleSaveClicked = bankAccount => {
    dispatch(saveBankAccount(ownerId, bankAccount));
  };

  const handleCancelClicked = () => {
    // If we're adding, switch back to the last viewed account (clamped).
    // If we're editing an existing account, EntityDetailsForm already exits
    // its update view on cancel; just stay on the same tab.
    if (isAdding && numOfAccounts > 0) {
      setActiveIndex(Math.min(lastViewedIndex, numOfAccounts - 1));
    }
  };

  // Track the last viewed account tab (not the "Add" pseudo-tab).
  const selectAccountTab = index => {
    setActiveIndex(index);
    setLastViewedIndex(index);
  };

  return (
    <div style={{ marginTop: '24px' }}>
      <div
        role="tablist"
        aria-label={formatMessage({ id: 'page.user.bankAccounts.title', defaultMessage: 'Bank Accounts' })}
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          margin: '0 auto 20px',
          maxWidth: '1110px',
        }}
      >
        {BANK_ACCOUNT_TEMPLATES.slice(0, numOfAccounts).map((template, index) => {
          const account = bankAccounts.find(a => a.ownerNumber === template.orderNumber);
          const isActive = safeActiveIndex === index && !isAdding;
          return (
            <button
              key={template.orderNumber}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => selectAccountTab(index)}
              style={isActive ? activeTabStyle : inactiveTabStyle}
            >
              <span style={statusDotStyle(!!account?.verified)} aria-hidden="true" />
              {formatMessage({ id: template.translationId })}
            </button>
          );
        })}
        {isAdding && (
          <button
            type="button"
            role="tab"
            aria-selected={true}
            style={activeTabStyle}
          >
            <span style={statusDotStyle(false)} aria-hidden="true" />
            {formatMessage({ id: activeTemplate.translationId })}
          </button>
        )}
        {canAddAnother && !isAdding && (
          <button
            type="button"
            role="tab"
            aria-selected={false}
            onClick={() => setActiveIndex(addIndex)}
            style={addTabStyle(false)}
          >
            <Icon name="plus" style={{ width: '14px', height: '14px' }} />
            {formatMessage({ id: 'page.user.bankAccounts.addAccount', defaultMessage: 'Add account' })}
          </button>
        )}
      </div>

      <EntityDetailsForm
        key={isAdding ? `new-${addIndex}` : (activeAccount?.id || activeTemplate.orderNumber)}
        columns={2}
        confirmationTitle={formatMessage({ id: 'page.user.myDetails.updateMessage' })}
        data={activeAccount}
        displayTopUpdateButton={!isAdding}
        enableUpdateView={isAdding}
        fields={bankAccountFieldsWithOptions}
        labelIds={{ buttons: { confirm: 'general.confirm' } }}
        warningMessage={warning}
        onCancel={handleCancelClicked}
        onChange={handleValuesChanged}
        onSave={handleSaveClicked}
      />
    </div>
  );
};

export default BankAccounts;
